import React from 'react';

import { QueryParams, useQuery } from '@betterment/js-runtime';

import { StateAgeLimitEligibility } from './data';

import { urlBuilder } from '../Shared/urlBuilder';

interface StateAgeLimitEligibiltyQueryParams {
  isProspect?: boolean;
  isForSecondary?: boolean;
  preferredFirstName?: string;
  stateOfResidence?: string;
  dateOfBirth?: string;
}

interface StateAgeLimitEligibilityCheckParams extends StateAgeLimitEligibiltyQueryParams {
  onEligibility?: () => void;
  onRequestError?: () => void;
}

type Status = 'loading' | 'eligible' | 'ineligible' | 'errored';

function assertNever(): never {
  throw new Error('impossible state');
}

export const useStateAgeLimitEligibilityCheck = ({
  onEligibility,
  onRequestError,
  ...queryParams
}: StateAgeLimitEligibilityCheckParams | undefined = {}) => {
  const {
    data,
    errors,
    loading: isLoading,
  } = useQuery<StateAgeLimitEligibility>(urlBuilder.apiV2CheckingStateAgeLimitEligibility.absolute(), {
    qs: queryParams as QueryParams,
  });

  const status: Status = (() => {
    switch (true) {
      case isLoading:
        return 'loading';
      case !!errors:
        return 'errored';
      case !!data?.isEligible:
        return 'eligible';
      case !data?.isEligible:
        return 'ineligible';
      default:
        return assertNever();
    }
  })();

  React.useEffect(() => {
    if (status === 'eligible') {
      onEligibility?.();
    }
    if (status === 'errored') {
      onRequestError?.();
    }
  }, [status, onEligibility, onRequestError]);

  return {
    status,
    ineligibilityMessage: {
      title: data?.ineligibilityMessage?.title ?? '',
      description: data?.ineligibilityMessage?.description ?? '',
    },
  };
};
